import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi, AuthAPIError } from '@/lib/auth-api';
import { Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import type { AuthErrorType } from '@/types';

interface LoginFormProps {
	redirectTo: string;
	onForgotPassword: () => void;
}

export function LoginForm({ redirectTo, onForgotPassword }: LoginFormProps) {
	const { t } = useTranslation();
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [remember, setRemember] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [errorType, setErrorType] = useState<AuthErrorType | null>(null);
	const [retryAfter, setRetryAfter] = useState<number | null>(null);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			setError(null);
			setErrorType(null);
			setRetryAfter(null);
			setIsLoading(true);

			try {
				const response = await authApi.login({
					username,
					password,
					remember,
				});

				if (response.success) {
					// Redirect to app on success
					const finalRedirect = response.redirect_to || redirectTo;
					window.location.href = finalRedirect;
				} else {
					setError(response.message || t('Prihlásenie zlyhalo'));
					setErrorType('invalid_credentials');
				}
			} catch (err) {
				if (err instanceof AuthAPIError) {
					setError(err.message);
					setErrorType(err.errorType);

					// Handle specific error types
					if (err.errorType === 'rate_limited' && err.retryAfter) {
						setRetryAfter(err.retryAfter);
					}

					// Handle nonce expiration - reload the page to get a fresh nonce
					if (err.errorType === 'nonce_expired') {
						setError(t('Platnosť relácie vypršala. Stránka sa obnoví...'));
						setTimeout(() => {
							window.location.reload();
						}, 1500);
					}
				} else {
					setError(t('Nastala neočakávaná chyba'));
					setErrorType('unknown');
				}
			} finally {
				setIsLoading(false);
			}
		},
		[username, password, remember, redirectTo, t]
	);

	const formatRetryTime = (seconds: number): string => {
		const minutes = Math.floor(seconds / 60);
		const secs = seconds % 60;
		if (minutes > 0) {
			return `${minutes} min ${secs} s`;
		}
		return `${secs} s`;
	};

	return (
		<form data-ui="login-form" onSubmit={handleSubmit} className="space-y-4">
			{/* Error Alert */}
			{error && (
				<Alert data-ui="login-form-error-alert" variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						{error}
						{errorType === 'rate_limited' && retryAfter && (
							<span className="block mt-1 text-sm">
								{t('Skúste to znova za {{time}}', {
									time: formatRetryTime(retryAfter),
								})}
							</span>
						)}
					</AlertDescription>
				</Alert>
			)}

			{/* Username */}
			<div className="space-y-2">
				<Label htmlFor="username">{t('Prihlasovacie meno alebo email')}</Label>
				<Input
					id="username"
					data-ui="login-form-username-input"
					type="text"
					value={username}
					onChange={(e) => setUsername(e.target.value)}
					placeholder={t('Zadajte meno alebo email')}
					autoComplete="username"
					autoFocus
					disabled={isLoading || errorType === 'rate_limited'}
					required
				/>
			</div>

			{/* Password */}
			<div className="space-y-2">
				<Label htmlFor="password">{t('Heslo')}</Label>
				<div className="relative">
					<Input
						id="password"
						data-ui="login-form-password-input"
						type={showPassword ? 'text' : 'password'}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						placeholder={t('Zadajte heslo')}
						autoComplete="current-password"
						disabled={isLoading || errorType === 'rate_limited'}
						required
						className="pr-10"
					/>
					<button
						type="button"
						data-ui="login-form-toggle-password-btn"
						onClick={() => setShowPassword(!showPassword)}
						className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
						tabIndex={-1}
					>
						{showPassword ? (
							<EyeOff className="h-4 w-4" />
						) : (
							<Eye className="h-4 w-4" />
						)}
					</button>
				</div>
			</div>

			{/* Remember me & Forgot password */}
			<div className="flex items-center justify-between">
				<div className="flex items-center space-x-2">
					<Checkbox
						id="remember"
						data-ui="login-form-remember-checkbox"
						checked={remember}
						onCheckedChange={(checked) => setRemember(checked === true)}
						disabled={isLoading || errorType === 'rate_limited'}
					/>
					<Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
						{t('Zapamätať si ma')}
					</Label>
				</div>
				<button
					type="button"
					data-ui="login-form-forgot-password-btn"
					onClick={onForgotPassword}
					className="text-sm text-primary hover:underline"
					disabled={isLoading}
				>
					{t('Zabudli ste heslo?')}
				</button>
			</div>

			{/* Submit */}
			<Button
				type="submit"
				data-ui="login-form-submit-btn"
				className="w-full"
				disabled={isLoading || errorType === 'rate_limited' || !username || !password}
			>
				{isLoading ? (
					<>
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						{t('Prihlasujem...')}
					</>
				) : (
					t('Prihlásiť sa')
				)}
			</Button>
		</form>
	);
}
